import React, { useState } from "react";
import ReactDOM from "react-dom";
import { api } from "../../../services/api";
import Avatar from "../../../components/Avatar/Avatar";
import styles from "./LobbyDetailModal.module.scss";
import type { BrowseListingView } from "../../../services/api/LfgApi";
import noImage from "../../../assets/no-image.jpg";

interface LobbyDetailModalProps {
  listing: BrowseListingView;
  onClose: () => void;
  onJoin?: () => void;
}

const LobbyDetailModal: React.FC<LobbyDetailModalProps> = ({ listing, onClose, onJoin }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasSlots = listing.memberCount < listing.maxPlayers;

  const handleJoin = async () => {
    setLoading(true);
    setError(null);
    try {
      await api.lobby.joinLobby(listing.lobbyId);
      onClose();
      onJoin?.();
    } catch (err: any) {
      setError(err?.message || "Failed to join lobby");
    } finally {
      setLoading(false);
    }
  };

  return ReactDOM.createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.card} onClick={(e) => e.stopPropagation()}>
        <div className={styles.coverSection}>
          <img
            src={listing.gameCoverUrl || noImage}
            alt={listing.gameName || "Game"}
            className={styles.coverArt}
            onError={(e) => {
              if (e.currentTarget.src !== noImage) {
                e.currentTarget.src = noImage;
              }
            }}
          />
          <div className={styles.coverOverlay}>
            <span className={styles.gameName}>{listing.gameName || "Game"}</span>
          </div>
        </div>

        <div className={styles.content}>
          <h2 className={styles.title}>{listing.title}</h2>

          {listing.description && (
            <p className={styles.description}>{listing.description}</p>
          )}

          <div className={styles.details}>
            <div className={styles.detailRow}>
              <span className={styles.label}>Host</span>
              <div className={styles.hostInfo}>
                <Avatar
                  src={listing.hostAvatarUrl}
                  username={listing.hostUsername}
                  size="xs"
                />
                <span>{listing.hostUsername}</span>
              </div>
            </div>

            <div className={styles.detailRow}>
              <span className={styles.label}>Players</span>
              <span className={styles.value}>{listing.memberCount} / {listing.maxPlayers}</span>
            </div>

            {listing.preferences?.gameMode && (
              <div className={styles.detailRow}>
                <span className={styles.label}>Mode</span>
                <span className={styles.value}>{listing.preferences.gameMode}</span>
              </div>
            )}

            {listing.preferences?.region && (
              <div className={styles.detailRow}>
                <span className={styles.label}>Region</span>
                <span className={styles.value}>{listing.preferences.region}</span>
              </div>
            )}

            {listing.preferences?.skillLevel && (
              <div className={styles.detailRow}>
                <span className={styles.label}>Skill</span>
                <span className={styles.value}>{listing.preferences.skillLevel}</span>
              </div>
            )}

            {listing.preferences?.voiceRequired && (
              <div className={styles.detailRow}>
                <span className={styles.label}>Voice</span>
                <span className={styles.value}>Required</span>
              </div>
            )}
          </div>

          <div className={styles.actions}>
            {hasSlots ? (
              <button
                className={styles.joinButton}
                onClick={handleJoin}
                disabled={loading}
              >
                {loading ? "Joining..." : "Join Lobby"}
              </button>
            ) : (
              <span className={styles.fullBadge}>Lobby Full</span>
            )}
            <button className={styles.closeButton} onClick={onClose}>Close</button>
          </div>

          {error && <p className={styles.error}>{error}</p>}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default LobbyDetailModal;
